/**
 * Ableitung der Anzeige-Rolle aus den Keycloak-JWT-Claims.
 *
 * WICHTIG: Diese Logik ist **rein kosmetisch**. Autoritativ ist ausschliesslich
 * das Backend (`backend/src/middleware/keycloakAuth.ts`) — hier wird nur
 * entschieden, was die Oberflaeche anzeigt bzw. ausblendet. Beide Seiten leiten
 * aus denselben Claim-Namen ab und muessen synchron gepflegt werden.
 */
import type { UserRole } from '@/types';

// ═══════════════════════════════════════════════════════
// Konfiguration
// ═══════════════════════════════════════════════════════

export interface RoleConfig {
  /** Tenants, deren Mitglieder automatisch GLOBAL_ADMIN sind. */
  adminTenants: string[];
  /** Email-Muster (mit optionalem `*`), die automatisch GLOBAL_ADMIN ergeben. */
  adminEmails: string[];
}

/** Zerlegt eine komma-getrennte ENV-Liste; trimmt und verwirft leere Eintraege. */
export function parseAdminList(raw: string | undefined, opts?: { lowercase?: boolean }): string[] {
  return (raw ?? '')
    .split(',')
    .map((entry) => (opts?.lowercase ? entry.trim().toLowerCase() : entry.trim()))
    .filter((entry) => entry.length > 0);
}

/** Konfiguration aus der Build-Zeit-ENV (Vite inlined `import.meta.env`). */
export const envRoleConfig: RoleConfig = {
  adminTenants: parseAdminList(import.meta.env.VITE_ADMIN_TENANTS as string | undefined),
  adminEmails: parseAdminList(import.meta.env.VITE_ADMIN_EMAILS as string | undefined, {
    lowercase: true,
  }),
};

// ═══════════════════════════════════════════════════════
// Hilfsfunktionen
// ═══════════════════════════════════════════════════════

function toArray(val: unknown): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((v): v is string => typeof v === 'string');
  if (typeof val === 'string') return [val];
  return [];
}

/** Wandelt ein Glob-Muster (nur `*` als Wildcard) in eine ankerte RegExp. */
function globToRegExp(pattern: string): RegExp {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\\*/g, '.*');
  return new RegExp(`^${escaped}$`);
}

/** Normalisiert Keycloak-Group-Pfade: "/Kunden/Targomo" -> [..., "Targomo"]. */
export function expandGroupNames(values: string[]): string[] {
  const out: string[] = [];
  for (const v of values) {
    out.push(v);
    const noLeading = v.startsWith('/') ? v.slice(1) : v;
    if (noLeading !== v) out.push(noLeading);
    const last = noLeading.split('/').pop();
    if (last && last !== noLeading) out.push(last);
  }
  return out;
}

function hasAdminTenant(allTenants: string[], groupNames: string[], adminTenants: string[]): boolean {
  if (adminTenants.length === 0) return false;
  const userTenants = new Set([...allTenants, ...expandGroupNames(groupNames)]);
  return adminTenants.some((t) => userTenants.has(t));
}

function hasAdminEmail(email: unknown, adminEmails: string[]): boolean {
  if (adminEmails.length === 0 || typeof email !== 'string') return false;
  const normalized = email.trim().toLowerCase();
  if (normalized.length === 0) return false;
  return adminEmails.some((entry) =>
    entry.includes('*') ? globToRegExp(entry).test(normalized) : entry === normalized
  );
}

// ═══════════════════════════════════════════════════════
// Rollenableitung
// ═══════════════════════════════════════════════════════

export interface DerivedRole {
  role: UserRole;
  tenant: string | null;
  tenants: string[];
}

/**
 * Leitet Rolle und Tenants aus den JWT-Claims ab.
 * `config` ist injizierbar, damit die Logik ohne Build-ENV testbar bleibt.
 */
export function determineRole(
  claims: Record<string, unknown>,
  config: RoleConfig = envRoleConfig
): DerivedRole {
  const realmAccess = claims.realm_access as { roles?: string[] } | undefined;
  const roles = realmAccess?.roles ?? [];
  const manageArr = toArray(claims.manage_docklylocate);
  const internalUserArr = toArray(claims.internal_user_docklylocate);
  const useArr = toArray(claims.use_docklylocate);
  const groupArr = toArray(claims.groups);

  const allTenants = [...new Set([...useArr, ...internalUserArr, ...manageArr])];

  if (
    roles.includes('global_admin') ||
    hasAdminTenant(allTenants, groupArr, config.adminTenants) ||
    hasAdminEmail(claims.email, config.adminEmails)
  ) {
    return { role: 'GLOBAL_ADMIN', tenant: allTenants[0] ?? null, tenants: allTenants };
  }
  if (manageArr.length > 0) {
    return { role: 'MANAGER', tenant: manageArr[0]!, tenants: allTenants };
  }
  if (internalUserArr.length > 0) {
    return { role: 'USER', tenant: internalUserArr[0]!, tenants: allTenants };
  }
  if (useArr.length > 0) {
    return { role: 'VIEWER', tenant: useArr[0]!, tenants: allTenants };
  }

  return { role: 'VIEWER', tenant: null, tenants: [] };
}

// ═══════════════════════════════════════════════════════
// Rollen-Hierarchie
// ═══════════════════════════════════════════════════════

const ROLE_HIERARCHY: Record<UserRole, number> = {
  GLOBAL_ADMIN: 4,
  MANAGER: 3,
  USER: 2,
  VIEWER: 1,
};

/** True, wenn `userRole` mindestens so hoch ist wie `requiredRole`. */
export function hasMinRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}
